/**
 * The compact chart's layout arithmetic (D-043), as pure functions over
 * numbers `SurveyChart.tsx` measures off the rendered card.
 *
 * The brief's rule: text never bleeds, overlaps, or gets clipped, and the fix
 * for a tight fit is never a smaller font or a hidden overflow — it is fewer
 * labels, or a stacked layout, with the exact values kept reachable. Every
 * one of those decisions is made HERE, from real rendered widths, so a unit
 * test can pin the arithmetic without a browser and the component only has
 * to measure honestly.
 */

/**
 * Which of `n` evenly pitched axis labels to print.
 *
 * `widths` are the labels' rendered widths in CSS px, `pitch` the width of
 * one slot (bars share the plot equally), `current` the index of the period
 * `now` falls in. The stride is the smallest whole number of slots that fits
 * the WIDEST label plus a breathing `gap` — every other month at a phone's
 * width, every month on a tablet, every third under a 200% text setting.
 * Ticks land on every `stride`th slot from the first, and the current
 * period's label is always printed (it is the bold one); a stride tick
 * closer to it than one stride would collide, so the current label wins and
 * that neighbour is dropped.
 *
 * Before layout (`pitch` 0) the stride is the whole set — the current label
 * only — which is also the honest answer when nothing has been measured.
 */
export function visibleTicks(
  widths: readonly number[],
  pitch: number,
  current: number,
  gap = 6,
): boolean[] {
  const n = widths.length;
  if (n === 0) return [];
  const widest = Math.max(0, ...widths);
  const stride = pitch > 0 ? Math.max(1, Math.ceil((widest + gap) / pitch)) : n;
  const shown = widths.map((_, index) => {
    if (index === current) return true;
    if (index % stride !== 0) return false;
    return Math.abs(index - current) >= stride;
  });

  // A label wider than its slot hugs the plot's edge instead of centring on
  // its bar (theme.css, `.chart--hug`, which the component sets on the same
  // `widest > pitch` test), which moves it inward by half the difference — toward the
  // next printed label. Where that closes the gap the stride promised, the
  // neighbour gives way; the current label never does, so if the neighbour
  // IS the current label the edge label goes instead.
  const shift = Math.max(0, (widest - pitch) / 2);
  if (shift > 0 && pitch > 0) {
    const yield_ = (edge: number, step: number): void => {
      if (!shown[edge]) return;
      let neighbour = edge + step;
      while (neighbour >= 0 && neighbour < n && !shown[neighbour]) neighbour += step;
      if (neighbour < 0 || neighbour >= n) return;
      if (Math.abs(neighbour - edge) * pitch - shift >= widest + gap) return;
      if (neighbour === current) shown[edge] = edge === current;
      else shown[neighbour] = false;
    };
    yield_(0, 1);
    yield_(n - 1, -1);
  }
  return shown;
}

/**
 * Whether every value annotation fits above its own bar: the widest printed
 * count is no wider than one slot less a `gap`. All or nothing on purpose —
 * a plot that prints "9" over one bar and nothing over the "128" beside it
 * reads as a missing value, not a tight fit. When this is false the card
 * hides every annotation and offers the counts as a list instead
 * (`SurveyChart.tsx`), so no number is ever lost, only relocated.
 */
export function valuesFit(widths: readonly number[], pitch: number, gap = 2): boolean {
  return widths.every((width) => width <= pitch - gap);
}

/**
 * The summary row's two columns share the card `1 : SUMMARY_SPLIT`, left to
 * right — the total's column is the narrower one because a five-digit total
 * still fits it at 54px, and the period column carries three lines of
 * shorter text that benefit from the room.
 */
export const SUMMARY_SPLIT = 1.16;

/**
 * Whether the summary row has to stack — the total above the period reading
 * instead of beside it. It does when the total (which cannot wrap: it is one
 * number) would not fit its column, or when the change reading's own
 * unbreakable text ("+50%", "−1 from 1,234") would not fit the other.
 * `inset` is the period column's divider and padding, `gap` the grid's.
 */
export function summaryStacks(args: {
  totalWidth: number;
  deltaWidth: number;
  summaryWidth: number;
  gap: number;
  inset: number;
}): boolean {
  const { totalWidth, deltaWidth, summaryWidth, gap, inset } = args;
  const left = (summaryWidth - gap) / (1 + SUMMARY_SPLIT);
  const right = summaryWidth - gap - left - inset;
  return totalWidth > left || deltaWidth > right;
}
